import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import "./Register.css";

// Inscription Artkiv — parcours en 2 étapes (compte → formule), au style éditorial
// du Login (2 colonnes, or #c9a961 / crème / encre, Georgia serif). Principe Origin :
// un seul sujet par écran, information dominante, une action principale.
//   • Invitation collaborateur (collab_token) : étape unique (compte), pas de plan.
//   • Compte démo : étape unique (formule), changement immédiat sans paiement.
//   • Inscription normale : étape 1 compte → étape 2 formule → création.

const inputCls = "reg-input"; // couleurs/focus portés par Register.css (cf. note Tailwind)

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Register() {
  const { t } = useTranslation();
  const { signUp, user } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);

  // Invitation collaborateur : si collab_token présent → pas d'abonnement.
  const collabToken = params.get("collab_token") || "";
  const isCollaboratorInvite = !!collabToken;
  const prePlan = (params.get("plan") || "standard").toLowerCase();

  const [step, setStep] = useState(1); // 1 = compte, 2 = formule
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [plan, setPlan] = useState(prePlan);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isDemo, setIsDemo] = useState(false);

  // Validation token + email invité
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [invitedEmail, setInvitedEmail] = useState("");

  const emailMismatch =
    isCollaboratorInvite &&
    invitedEmail &&
    email.trim().toLowerCase() !== invitedEmail.trim().toLowerCase();

  // Compte démo déjà connecté → on saute directement au choix de formule.
  useEffect(() => {
    const checkDemo = async () => {
      if (!user) return;
      const { data: profile } = await supabase
        .from("profiles")
        .select("is_demo")
        .eq("id", user.id)
        .single();
      if (profile?.is_demo) {
        setIsDemo(true);
        setEmail(user.email || "");
        setStep(2);
      }
    };
    checkDemo();
  }, [user]);

  useEffect(() => {
    if (isCollaboratorInvite) return; // pas de plan forcé en mode collaborateur
    setPlan(prePlan);
  }, [prePlan, isCollaboratorInvite]);

  useEffect(() => {
    if (!isCollaboratorInvite) return;
    let mounted = true;
    (async () => {
      try {
        setInviteLoading(true);
        setInviteError("");
        setInvitedEmail("");
        const { data, error } = await supabase
          .from("collaborators")
          .select("email,status,invitation_accepted_at,invitation_token")
          .eq("invitation_token", collabToken)
          .single();
        if (error || !data) throw new Error(t("register.errors.invalidInviteLink"));
        if (data.status === "revoked") throw new Error(t("register.errors.inviteRevoked"));
        if (data.invitation_accepted_at) throw new Error(t("register.errors.inviteAlreadyUsed"));
        if (mounted) setInvitedEmail(data.email || "");
      } catch (e) {
        if (mounted) setInviteError(e.message || t("register.errors.invalidInvite"));
      } finally {
        if (mounted) setInviteLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [isCollaboratorInvite, collabToken, t]);

  // Offre de lancement (jusqu'au 15/02/2026) — logique conservée telle quelle.
  const DEADLINE_ISO = "2026-02-15T23:59:59Z";
  const trialWindowActive = useMemo(() => new Date() < new Date(DEADLINE_ISO), []);

  // Étape 1 → 2 : validation locale du compte avant d'afficher les formules.
  const goToPlan = (e) => {
    e.preventDefault();
    setError("");
    if (!EMAIL_RE.test(email.trim())) { setError(t("register.emailInvalid")); return; }
    if (password.length < 6) { setError(t("register.passwordShort")); return; }
    if (emailMismatch) { setError(t("register.invite.emailMismatch")); return; }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isCollaboratorInvite && isDemo) {
        throw new Error(t("register.errors.demoInviteIncompatible"));
      }

      // Compte démo : on change simplement le plan, sans paiement.
      if (isDemo && user) {
        const { error: updateError } = await supabase
          .from("profiles")
          .update({ plan, stripe_subscription_status: "active" })
          .eq("id", user.id);
        if (updateError) throw updateError;
        alert(t("register.demo.planUpdated"));
        navigate("/dashboard");
        return;
      }

      // Création du compte. Le prénom et l'éventuel collab_token passent en
      // metadata (raw_user_meta_data) — récupérables par le trigger handle_new_user.
      const metadata = {};
      if (firstName.trim()) metadata.first_name = firstName.trim();
      if (isCollaboratorInvite) metadata.collab_token = collabToken;

      const signUpResult = await signUp({
        email,
        password,
        options: Object.keys(metadata).length ? { data: metadata } : undefined,
      });
      if (signUpResult.error) throw signUpResult.error;

      const newUser = signUpResult.user || signUpResult.data?.user;
      if (!newUser || !newUser.id) throw new Error(t("register.errors.userNotCreated"));
      const userId = newUser.id;

      // On s'appuie sur le trigger handle_new_user (SECURITY DEFINER) — pas
      // d'insert profiles depuis le client (RLS + race condition).
      localStorage.setItem("pendingEmail", email);
      if (!isCollaboratorInvite) localStorage.setItem("pendingPlan", plan);
      localStorage.setItem("pendingUserId", userId);
      if (isCollaboratorInvite) localStorage.setItem("pendingCollabToken", collabToken);

      if (isCollaboratorInvite) {
        navigate(`/collaborators/accept?token=${encodeURIComponent(collabToken)}`);
        return;
      }
      // Family Office = vente directe (contact commercial), pas de paiement en ligne.
      if (plan === "family_office" || plan === "fondation") {
        navigate(`/contact?plan=${plan}&from=register`);
        return;
      }
      navigate(`/email-confirmation?email=${encodeURIComponent(email)}&plan=${plan}`);
    } catch (err) {
      console.error("Erreur inscription:", err);
      setError(err.message || t("register.errors.signup"));
      setLoading(false);
    }
  };

  const collectorPlans = [
    { id: "standard", name: t("register.plans.standard.name"), price: "29 €", per: true, desc: t("register.plans.standard.description"), trial: trialWindowActive ? t("register.plans.standard.trial") : "" },
    { id: "premium", name: t("register.plans.premium.name"), price: "199 €", per: true, desc: t("register.plans.premium.description"), badge: t("register.plans.premium.badge") },
  ];
  const proPlans = [
    { id: "art_advisor", name: t("register.plans.artAdvisor.name"), price: "99 €", per: true, desc: t("register.plans.artAdvisor.description"), badge: t("register.plans.artAdvisor.badge") },
    { id: "art_advisor_premium", name: t("register.plans.artAdvisorPremium.name"), price: "199 €", per: true, desc: t("register.plans.artAdvisorPremium.description"), badge: t("register.plans.artAdvisorPremium.badge") },
    { id: "fondation", name: t("register.plans.fondation.name"), price: "299 €", per: true, desc: t("register.plans.fondation.description"), note: t("register.plans.fondation.note"), badge: t("register.plans.fondation.badge") },
    { id: "family_office", name: t("register.plans.familyOffice.name"), price: t("register.plans.familyOffice.price"), per: false, desc: t("register.plans.familyOffice.description"), badge: t("register.plans.familyOffice.badge") },
  ];

  const PlanRow = ({ p, delay = 0 }) => {
    const selected = plan === p.id;
    return (
      <label
        style={{ animationDelay: `${delay * 90}ms` }}
        className={"reg-in reg-plan flex items-start justify-between gap-4 rounded-xl p-4 cursor-pointer " + (selected ? "reg-plan--sel shadow-sm" : "")}
      >
        <div className="flex items-start gap-3">
          <span className="reg-dot mt-1 w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center" aria-hidden="true">
            {selected && <span className="w-2 h-2 rounded-full" style={{ background: "#c9a961" }} />}
          </span>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold" style={{ color: "#1a1a1a" }}>{p.name}</span>
              {p.badge && (
                <span className="reg-badge px-2 py-0.5 text-[10px] font-bold tracking-wide rounded">
                  {p.badge}
                </span>
              )}
            </div>
            <div className="reg-muted2 text-xs mt-0.5">{p.desc}</div>
            {p.note && <div className="reg-muted2 text-xs mt-1 italic">{p.note}</div>}
            {p.trial && (
              <div className="flex items-center gap-1 text-xs font-semibold mt-1.5" style={{ color: "#3b7d4f" }}>
                <span aria-hidden="true">✓</span>
                <span>{p.trial}</span>
              </div>
            )}
          </div>
        </div>
        <div className="text-right whitespace-nowrap">
          <span className="font-bold" style={{ color: "#1a1a1a" }}>{p.price}</span>
          {p.per && <span className="reg-muted text-xs font-normal">{t("register.plans.perMonth")}</span>}
        </div>
        <input
          type="radio"
          name="plan"
          value={p.id}
          checked={selected}
          onChange={() => setPlan(p.id)}
          className="sr-only"
        />
      </label>
    );
  };

  // Formulaire compte (étape 1 / mode collaborateur).
  const AccountFields = (
    <div className="space-y-5 reg-stagger">
      {!isDemo && (
        <div>
          <label htmlFor="firstName" className="reg-muted block text-xs uppercase tracking-wide mb-2">
            {t("register.firstNameLabel")}
          </label>
          <input
            id="firstName"
            type="text"
            className={inputCls}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="given-name"
            placeholder={t("register.firstNamePlaceholder")}
          />
        </div>
      )}

      <div>
        <label htmlFor="email" className="reg-muted block text-xs uppercase tracking-wide mb-2">
          {t("register.fields.email")}
        </label>
        <input
          id="email"
          type="email"
          className={inputCls}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required={!isDemo}
          disabled={isDemo}
          autoComplete="email"
          inputMode="email"
          placeholder={t("register.placeholders.email")}
        />
      </div>

      {!isDemo && (
        <div>
          <label htmlFor="password" className="reg-muted block text-xs uppercase tracking-wide mb-2">
            {t("register.fields.password")}
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPw ? "text" : "password"}
              className={inputCls}
              style={{ paddingRight: "3.5rem" }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              minLength={6}
              placeholder={t("register.placeholders.password")}
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="reg-muted absolute right-3 top-1/2 -translate-y-1/2 text-xs"
              aria-label={showPw ? t("register.hidePw") : t("register.showPw")}
            >
              {showPw ? t("register.hidePw") : t("register.showPw")}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const showPlanStep = !isCollaboratorInvite && (isDemo || step === 2);
  const showAccountStep = !isDemo && (isCollaboratorInvite || step === 1);
  const totalSteps = 2;
  const currentStep = showPlanStep && !isDemo ? 2 : 1;

  return (
    <div className="flex flex-col lg:flex-row min-h-screen" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
      {/* Colonne gauche — panneau éditorial */}
      <div className="lg:w-1/2 relative hidden lg:flex reg-panel overflow-hidden">
        <img
          src={`${process.env.PUBLIC_URL}/images/ui/login_bg.png`}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.55 }}
        />
        <div className="reg-hero-scrim" />
        <div className="reg-stagger reg-stagger--hero relative z-10 flex flex-col justify-center px-14 xl:px-20 py-16 max-w-xl">
          <div className="reg-gold text-xs tracking-[0.35em] uppercase mb-8">{t("register.heroEyebrow")}</div>
          <p className="reg-cream text-3xl xl:text-4xl leading-[1.25] font-normal" style={{ textShadow: "0 1px 16px rgba(0,0,0,0.45)" }}>{t("register.heroCopy")}</p>
          <div className="reg-hr-gold mt-10 h-px w-16" style={{ opacity: 0.7 }} />
          <ul className="reg-cream mt-10 space-y-3 text-base" style={{ opacity: 0.9 }}>
            <li className="flex items-center gap-3"><span className="reg-gold">—</span>{t("register.heroPoint1")}</li>
            <li className="flex items-center gap-3"><span className="reg-gold">—</span>{t("register.heroPoint2")}</li>
            <li className="flex items-center gap-3"><span className="reg-gold">—</span>{t("register.heroPoint3")}</li>
          </ul>
        </div>
      </div>

      {/* Colonne droite — formulaire */}
      <div className="lg:w-1/2 flex items-center justify-center px-6 py-12 lg:p-16 bg-[#f9f7f4]">
        <div className="w-full max-w-xl">
          {/* En-tête */}
          <div className="reg-in mb-8">
            {!isCollaboratorInvite && !isDemo && (
              <div className="reg-muted text-xs tracking-[0.14em] uppercase mb-3">
                {t("register.stepProgress", { current: currentStep, total: totalSteps })}
              </div>
            )}
            <h2 className="text-4xl font-bold leading-tight" style={{ color: "#1a1a1a" }}>
              {isCollaboratorInvite
                ? t("register.titleDelegated")
                : showPlanStep
                ? t("register.stepPlanTitle")
                : t("register.stepAccountTitle")}
            </h2>
            <div className="reg-hr-gold mt-4 h-px w-12" />
          </div>

          {/* Bandeaux contextuels */}
          {isCollaboratorInvite && (
            <p className="text-[#6b6459] mb-4">{t("register.invite.subtitle")}</p>
          )}
          {isCollaboratorInvite && invitedEmail && (
            <div className="reg-card p-3 rounded-lg text-sm mb-4">
              {t("register.invite.emailLabel")} <strong>{invitedEmail}</strong>
            </div>
          )}
          {isCollaboratorInvite && (inviteError || emailMismatch) && (
            <div className="reg-alert p-3 rounded-lg mb-4">
              <p className="text-sm">{inviteError || t("register.invite.emailMismatch")}</p>
            </div>
          )}
          {isDemo && (
            <div className="reg-note p-4 rounded-xl mb-6">
              <div className="text-sm">
                <strong className="block mb-1">{t("register.demo.title")}</strong>
                {t("register.demo.body")}
              </div>
            </div>
          )}
          {trialWindowActive && !isDemo && !isCollaboratorInvite && (
            <div className="reg-note p-4 rounded-xl mb-6">
              <div className="text-sm">
                <strong className="block mb-1">{t("register.launch.title")}</strong>
                <Trans i18nKey="register.launch.body" components={{ trial: <strong />, date: <strong /> }} />
              </div>
            </div>
          )}

          {/* ÉTAPE 1 — Compte (ou étape unique collaborateur) */}
          {showAccountStep && (
            <form onSubmit={isCollaboratorInvite ? handleSubmit : goToPlan}>
              {AccountFields}

              {error && (
                <div className="reg-alert mt-5 p-3 rounded-lg">
                  <p className="text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || (isCollaboratorInvite && (inviteLoading || !!inviteError || emailMismatch))}
                className="reg-btn-gold mt-6 w-full py-3.5 rounded-lg font-semibold"
              >
                {loading
                  ? t("register.buttons.creating")
                  : isCollaboratorInvite
                  ? t("register.buttons.createDelegated")
                  : t("register.continue")}
              </button>

              {isCollaboratorInvite && (
                <p className="mt-4 text-xs text-[#8a8378] text-center">
                  <Trans
                    i18nKey="register.legal.sentence"
                    components={{
                      // eslint-disable-next-line jsx-a11y/anchor-has-content -- contenu injecté par <Trans>
                      terms: <a href="/legal" className="underline hover:text-[#1a1a1a]" />,
                      // eslint-disable-next-line jsx-a11y/anchor-has-content -- contenu injecté par <Trans>
                      privacy: <a href="/privacy-policy" className="underline hover:text-[#1a1a1a]" />,
                    }}
                  />
                </p>
              )}

              {!isCollaboratorInvite && (
                <p className="mt-6 text-sm text-[#6b6459] text-center">
                  {t("register.login.prompt")}{" "}
                  <Link to="/login" className="underline font-medium hover:text-[#1a1a1a]">
                    {t("register.login.link")}
                  </Link>
                </p>
              )}
            </form>
          )}

          {/* ÉTAPE 2 — Choix de la formule */}
          {showPlanStep && (
            <form onSubmit={handleSubmit}>
              {/* Rappel du compte + retour (masqué en mode démo) */}
              {!isDemo && (
                <div className="reg-in reg-card flex items-center justify-between p-3 rounded-lg mb-6 text-sm">
                  <span className="reg-muted2 truncate">
                    {t("register.accountRecap")} : <strong style={{ color: "#1a1a1a" }}>{email}</strong>
                  </span>
                  <button
                    type="button"
                    onClick={() => { setError(""); setStep(1); }}
                    className="reg-gold hover:underline flex-shrink-0 ml-3"
                  >
                    {t("register.editAccount")}
                  </button>
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <div className="text-xs text-[#8a8378] uppercase tracking-wider mb-3">{t("register.sections.collectors")}</div>
                  <div className="space-y-3">
                    {collectorPlans.map((p, i) => <PlanRow key={p.id} p={p} delay={i} />)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-[#8a8378] uppercase tracking-wider mb-3">{t("register.sections.professionals")}</div>
                  <div className="space-y-3">
                    {proPlans.map((p, i) => <PlanRow key={p.id} p={p} delay={i + 2} />)}
                  </div>
                </div>
              </div>

              {error && (
                <div className="reg-alert mt-5 p-3 rounded-lg">
                  <p className="text-sm">{error}</p>
                </div>
              )}

              {["art_advisor", "art_advisor_premium", "family_office", "fondation"].includes(plan) && !isDemo && (
                <p className="reg-note mt-4 text-xs p-3 rounded-lg">
                  {t("register.professionalFollowup")}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="reg-btn-gold mt-6 w-full py-3.5 rounded-lg font-semibold"
              >
                {loading
                  ? (isDemo ? t("register.buttons.changing") : t("register.buttons.creating"))
                  : (isDemo ? t("register.buttons.changePlan") : t("register.buttons.createAccount"))}
              </button>

              {trialWindowActive && plan === "standard" && !isDemo && (
                <p className="mt-4 text-xs text-[#6b6459] text-center">{t("register.trialFootnote")}</p>
              )}

              {!isDemo && (
                <p className="mt-4 text-xs text-[#8a8378] text-center">
                  <Trans
                    i18nKey="register.legal.sentence"
                    components={{
                      // eslint-disable-next-line jsx-a11y/anchor-has-content -- contenu injecté par <Trans>
                      terms: <a href="/legal" className="underline hover:text-[#1a1a1a]" />,
                      // eslint-disable-next-line jsx-a11y/anchor-has-content -- contenu injecté par <Trans>
                      privacy: <a href="/privacy-policy" className="underline hover:text-[#1a1a1a]" />,
                    }}
                  />
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
